// The fleet as the screens need it: every VST sensor with its labels, latest
// temperature and state (from its active alarms and its last contact), the
// active alarms, and the location tree. Loaded once per account and language,
// refreshed every minute.
import { signal } from "@preact/signals";
import { useEffect } from "preact/hooks";
import { api, opsApi, type AlarmRow, type DeviceSummary } from "@gimasi/giothub-shell";
import { accounts, accountsReady, currentAccount, tenantApi } from "../lib/account";
import { currentInstalls, loadInstalls, type Install } from "./installs";
import { loadRulesModel } from "./rules";
import { loadStores, type Store } from "./stores";
import { loadCabinets, type Cabinet } from "./cabinets";
import { buildTree, type LocNode, type Point, type St, worse } from "./locations";
import { unwrap } from "../lib/value";

// alarm type -> state (rules of tools/setup-rules.py)
export const ALARM_ST: Record<string, St> = { TEMP_CRITICAL: "crit", TEMP_HIGH: "alarm", TEMP_WARNING: "warn", TEMP_LOW: "warn", NO_DATA: "delayed" };
export const DELAYED_MIN = 30;

const val = (v: unknown): number | null => {
  const x = unwrap(v);
  return typeof x === "number" ? x : x == null || x === "" ? null : Number.isFinite(Number(x)) ? Number(x) : null;
};

export interface Fleet { points: Point[]; alarms: AlarmRow[]; root: LocNode; byId: Map<string, Point>; at: number; installs: Install[]; stores: Store[]; cabinets: Cabinet[] }
export const fleet = signal<Fleet | null>(null);
export const fleetError = signal<string>("");
let loading: Promise<void> | null = null;
let key = "";

async function names(lang: string) {
  const out: Record<string, Record<string, string>> = {};
  await Promise.all(["country", "city", "area", "site"].map(async (k) => {
    try {
      const r = await tenantApi<{ items: { value: string; label?: string; name?: string }[] }>("GET", `/labels/${k}/values?lang=${lang}`);
      out[k] = Object.fromEntries(r.items.map((x) => [x.value, x.label || x.name || x.value]));
    } catch { /* names fall back to the slug */ }
  }));
  return out;
}

/**
 * owner per device: the deepest account whose (account-limited) list holds it. The API has no owner
 * field, so this asks every account's list; a device whose lookup failed keeps the owner found
 * last time instead of falling back to the root (the Customer column must not flicker).
 */
let known = new Map<string, string>();
async function owners(): Promise<Map<string, string>> {
  await accountsReady();
  const out = new Map<string, string>();
  let failed = false;
  const accs = [...accounts.value].filter((a) => a.parent_id).sort((a, b) => (a.ancestors?.length || 0) - (b.ancestors?.length || 0));
  const root = accounts.value.find((a) => !a.parent_id);
  const lists = await Promise.all(accs.map((a) => tenantApi<{ items: { device_id: string }[] }>("GET", `/fleet/devices?query=${encodeURIComponent(JSON.stringify({ page_size: 500 }))}`, undefined, a.id).then((r) => [a.id, r.items] as const, () => { failed = true; return [a.id, []] as const; })));
  for (const [id, items] of lists) for (const d of items) out.set(d.device_id, id); // deeper accounts come later and win
  if (failed) for (const [d, id] of known) if (!out.has(d)) out.set(d, id);
  if (root) out.set("__root", root.id);
  known = out;
  return out;
}

export async function loadFleet(lang: string) {
  const [devs, act, nm, own, inst, , cat, cabs] = await Promise.all([
    api.fleetDevices({ page_size: 500, sort: "name", keys: ["temperature_1", "ext_temperature", "ext_humidity", "battery_soc_pct", "fw_version", "link.rssi_dbm", "link.snr_db", "link.gateway"] }),
    opsApi.alarms({ state: "active", limit: 500 }),
    names(lang),
    owners().catch(() => known),
    loadInstalls().catch(() => [] as Install[]),
    loadRulesModel().catch(() => null), // regime limits from the live rules
    loadStores(lang).catch(() => [] as Store[]),
    loadCabinets().catch(() => [] as Cabinet[]),
  ]);
  // stores of the accounts this person can see (label values have no owner yet)
  const seen = new Set(accounts.value.map((a) => a.id));
  const sel = currentAccount.value;
  const under = (id: string) => !sel || id === sel || !!accounts.value.find((a) => a.id === id)?.ancestors?.includes(sel);
  const stores = cat.filter((x) => (!x.account_id || seen.has(x.account_id)) && (!x.account_id || under(x.account_id)));
  const cabinets = cabs.filter((x) => (!x.account_id || seen.has(x.account_id)) && (!x.account_id || under(x.account_id)));
  const cur = currentInstalls(inst);
  const alarmSt = new Map<string, St>();
  for (const a of act.items) alarmSt.set(a.device_id, worse(alarmSt.get(a.device_id) || "ok", ALARM_ST[a.type] || "warn"));
  const now = Date.now();
  const points: Point[] = (devs.items as DeviceSummary[]).map((d) => {
    const temp = val(d.state?.ext_temperature) ?? val(d.state?.temperature_1);
    const quiet = !d.last_contact_utc || now - Date.parse(d.last_contact_utc) > DELAYED_MIN * 60e3;
    const st = worse(alarmSt.get(d.device_id) || "ok", quiet ? "delayed" : "ok");
    return { id: d.device_id, serial: d.device_serial, name: d.name || d.device_serial, kind: cur.get(d.device_id)?.body.room ? "room" as const : "cabinet" as const, humidity: val(d.state?.ext_humidity), regime: String(d.attributes?.cold_class || d.attributes?.temperature_regime || ""), temp,
      battery: val(d.state?.battery_soc_pct), rssi: val(d.state?.["link.rssi_dbm"]), snr: val(d.state?.["link.snr_db"]), gateway: (unwrap(d.state?.["link.gateway"]) as string | null) ?? null, lastContact: d.last_contact_utc, st,
      labels: { ...(d.attributes || {}), fw_version: unwrap(d.state?.fw_version) ?? "" },
      owner: own.get(d.device_id) || own.get("__root") || null, installedAt: cur.get(d.device_id)?.body.stock ? null : cur.get(d.device_id)?.occurred_at || null };
  });
  fleet.value = { points, alarms: act.items, root: buildTree(points, nm, stores, cabinets), byId: new Map(points.map((p) => [p.id, p])), at: now, installs: inst, stores, cabinets };
}

/** The fleet for the current account, reloaded on account or language change and every minute. */
export function useFleet(lang: string) {
  const acc = currentAccount.value;
  useEffect(() => {
    const k = acc + "|" + lang;
    const run = () => {
      if (!loading) loading = loadFleet(lang).then(() => { fleetError.value = ""; }, (e: Error) => { fleetError.value = e.message; }).finally(() => { loading = null; });
      return loading;
    };
    if (k !== key) { key = k; fleet.value = null; }
    run();
    const id = setInterval(run, 60_000);
    return () => clearInterval(id);
  }, [acc, lang]);
  return fleet.value;
}
